import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import Icon from "../../../../shared/components/Icon";
import { getChartTypesForReportType } from "../constants/chartOptions";

/**
 * Shared chart type dropdown. Options depend on reportType (insight vs funnel).
 * @param {"insight" | "funnel"} reportType
 * @param {string} chartType - current chart type id
 * @param {(id: string) => void} onChartTypeChange
 * @param {object} theme - analytics theme (border, textMuted, textPrimary, etc.)
 */
export default function ChartTypeDropdown({ reportType, chartType, onChartTypeChange, theme }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const chartTypes = getChartTypesForReportType(reportType || "insight");
  const current = chartTypes.find((ct) => ct.id === chartType) || chartTypes[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={buttonRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors"
        style={{
          backgroundColor: open ? "#f1f5f9" : "#ffffff",
          borderColor: theme?.border || "#DDE2EE",
          color: "#0f172a",
          fontFamily: "Raleway, sans-serif",
          fontSize: "14px",
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "#f1f5f9";
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "#ffffff";
        }}
      >
        {current.iconName ? (
          <Icon name={current.iconName} size={16} color="#0f172a" />
        ) : current.icon ? (
          <current.icon className="w-4 h-4" style={{ color: "#0f172a" }} />
        ) : null}
        <span>{current.label}</span>
        <ChevronDown
          size={16}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: "#475569" }}
        />
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute left-0 top-full mt-1 z-[100] min-w-[200px] border rounded-lg shadow-xl py-1"
          style={{
            fontFamily: "Raleway, sans-serif",
            backgroundColor: "#ffffff",
            borderColor: theme?.border || "#DDE2EE",
          }}
        >
          {chartTypes.map((ct) => {
            const isSelected = chartType === ct.id;
            const isDisabled = ct.disabled;
            return (
              <button
                key={ct.id}
                type="button"
                onClick={() => {
                  if (!isDisabled) {
                    onChartTypeChange(ct.id);
                    setOpen(false);
                  }
                }}
                disabled={isDisabled}
                className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors"
                style={{
                  backgroundColor: isSelected ? "rgba(15, 23, 42, 0.08)" : "transparent",
                  color: isSelected ? "#0f172a" : isDisabled ? "#475569" : "#475569",
                  opacity: isDisabled ? 0.5 : 1,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected && !isDisabled) {
                    e.currentTarget.style.backgroundColor = "rgba(15, 23, 42, 0.08)";
                    e.currentTarget.style.color = "#0f172a";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected && !isDisabled) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
              >
                {ct.iconName ? (
                  <Icon
                    name={ct.iconName}
                    size={16}
                    color={isSelected ? "#0f172a" : theme?.textMuted || "#475569"}
                  />
                ) : ct.icon ? (
                  <ct.icon
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: isSelected ? "#0f172a" : theme?.textMuted || "#475569" }}
                  />
                ) : null}
                <span className="flex-1">{ct.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
